'use client';

import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type FocusEvent,
} from 'react';
import { cn } from '@/lib/utils/cn';

export interface FloatingFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  invalid?: boolean;
  rightSlot?: ReactNode;
  containerClassName?: string;
  /** Variante compacte (padding + typo réduits). Utile pour les formulaires
   *  denses où la taille par défaut prend trop de hauteur.
   *   - `true` → toujours compact
   *   - `'mobile'` → compact uniquement < sm (640px), taille standard sinon */
  compact?: boolean | 'mobile';
}

export const FloatingField = forwardRef<HTMLInputElement, FloatingFieldProps>(
  function FloatingField(
    {
      id,
      label,
      error,
      invalid,
      rightSlot,
      containerClassName,
      compact = false,
      className,
      onFocus,
      onBlur,
      onChange,
      placeholder,
      defaultValue,
      value,
      ...props
    },
    ref,
  ) {
    const reactId = useId();
    const inputId = id ?? reactId;
    const [focused, setFocused] = useState(false);
    // RHF n'expose pas la valeur via les props (register fournit ref/onChange/onBlur
    // mais pas value/defaultValue) → on lit le DOM directement au mount via un
    // ref local, en plus du tracking onChange/onBlur classique. Indispensable pour
    // les écrans pré-remplis (SSO, edit) où le label flotterait sinon par-dessus
    // la valeur visible.
    const initialFilled =
      (typeof value === 'string' && value.length > 0) ||
      (typeof defaultValue === 'string' && defaultValue.length > 0);
    const [filled, setFilled] = useState(initialFilled);

    const internalRef = useRef<HTMLInputElement | null>(null);
    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        // RHF (et l'autofill SSO) pose la valeur sur le DOM via le ref sans
        // déclencher onChange. On lit la valeur ici, au mount, pour faire
        // flotter le label dès le premier render. onChange/onBlur prennent
        // ensuite le relais sur les transitions.
        if (node && node.value.length > 0) {
          setFilled(true);
        }
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const float = focused || filled;
    const errored = invalid ?? Boolean(error);

    // Le mode compact peut être activé en permanence (`true`) ou uniquement
    // en mobile (`'mobile'`). Pour mobile-only on combine les classes
    // compactes derrière `max-sm:` avec les défauts en `sm:`.
    const compactAlways = compact === true;
    const compactMobile = compact === 'mobile';
    const compactCls = (always: string, mobile: string, def: string): string => {
      if (compactAlways) return always;
      if (compactMobile) return cn(mobile, def);
      return def;
    };

    return (
      <div className={cn('flex flex-col gap-1', containerClassName)}>
        <label
          htmlFor={inputId}
          className={cn(
            'group bg-bg-surface relative block cursor-text rounded-[10px] border',
            'transition-[border-color,box-shadow,background-color] duration-150 ease-out',
            compactCls(
              'px-3 pt-[5px] pb-[5px]',
              'max-sm:px-3 max-sm:pt-[5px] max-sm:pb-[5px]',
              'px-3.5 pt-[8px] pb-[8px]',
            ),
            errored
              ? 'border-danger-500 shadow-[0_0_0_4px_rgba(220,53,69,0.12)]'
              : focused
                ? 'border-border-focus shadow-[var(--shadow-focus)]'
                : 'border-border-default hover:border-border-strong',
          )}
        >
          <span
            className={cn(
              'pointer-events-none absolute transition-all duration-150 ease-out',
              compactCls('left-3', 'max-sm:left-3', 'left-3.5'),
              float
                ? cn(
                    'font-medium tracking-[0.02em]',
                    compactCls(
                      'top-[4px] text-[10px]',
                      'max-sm:top-[4px] max-sm:text-[10px]',
                      'top-[7px] text-[11px]',
                    ),
                    errored ? 'text-danger-700' : focused ? 'text-purple-600' : 'text-fg-4',
                  )
                : cn(
                    'text-fg-5 top-1/2 -translate-y-1/2 font-normal',
                    compactCls('text-[12.5px]', 'max-sm:text-[12.5px]', 'text-sm'),
                  ),
            )}
          >
            {label}
          </span>
          <input
            {...props}
            id={inputId}
            ref={setRefs}
            // Sans aria-label explicite, le nom accessible est calculé depuis
            // le <label> parent qui englobe aussi le rightSlot (ex: bouton
            // "Afficher/Masquer" sur un champ password). On obtient alors
            // "Mot de passe Afficher", qui casse `getByLabel(label, exact)`
            // côté Playwright et brouille l'annonce des screen readers.
            aria-label={label}
            aria-invalid={errored || undefined}
            placeholder={focused ? placeholder : ''}
            value={value}
            defaultValue={defaultValue}
            onChange={(event) => {
              setFilled(event.target.value.length > 0);
              onChange?.(event);
            }}
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
              setFocused(true);
              onFocus?.(event);
            }}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setFocused(false);
              setFilled(event.target.value.length > 0);
              onBlur?.(event);
            }}
            className={cn(
              'text-fg-1 placeholder:text-fg-5 block w-full border-0 bg-transparent p-0 font-normal outline-none',
              compactCls(
                'pt-[14px] text-[12.5px]',
                'max-sm:pt-[14px] max-sm:text-[12.5px]',
                'pt-[18px] text-sm',
              ),
              rightSlot && compactCls('pr-8', 'max-sm:pr-8', 'pr-9'),
              className,
            )}
          />
          {rightSlot ? (
            <span className="absolute top-1/2 right-2 inline-flex -translate-y-1/2 items-center">
              {rightSlot}
            </span>
          ) : null}
        </label>
        {error ? <p className="text-danger-700 px-1 text-xs">{error}</p> : null}
      </div>
    );
  },
);
